import {
    clearProspectingViewTrackingContext,
    ProspectingViewTrackingContext,
    setProspectingViewTrackingContext
} from "analytics/analyticTrackEvents";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useRef
} from "react";
import { useParams } from "react-router-dom";
import {
    ComponentConfig,
    getComponentConfig,
    isPricedConstraintConfig,
    PricedConstraintConfig
} from "types/componentConfigPerRegion";
import { GeneratorType } from "types/generatorType";
import { RegionType } from "types/regionType";
import { getAcceptableScopesForRegionAndGenerator, Scope } from "types/scope";
import {
    getCapacityCostsScopeViewFromRegionAndGenerator,
    ScopeViewType
} from "types/scopeViewType";
import {
    convertToUrlDirectoryType,
    getGeneratorTypeFromUrlDirectory,
    getRegionFromUrlDirectory,
    isKnownUrlDirectoryType,
    UrlDirectoryType
} from "types/urlDirectoryType";

type RoutingMetadataType = "mapView" | "homeView";

interface RoutingMetadataContext {
    type: RoutingMetadataType;
}

type MapViewRoutingMetadataContext = RoutingMetadataContext & {
    type: "mapView";
    urlDirectory: UrlDirectoryType;
    region: RegionType;
    generator: GeneratorType;
    componentConfig: ComponentConfig;
    scopeView: ScopeViewType;
    acceptableScopes: ReadonlyArray<Scope>;
};

type HomeViewRoutingMetadataContext = RoutingMetadataContext & {
    type: "homeView";
};

const RoutingMetadataContext = createContext<
    RoutingMetadataContext | undefined
>(undefined);

const RoutingMetadataProvider: React.FC<PropsWithChildren<unknown>> = (
    props: PropsWithChildren<unknown>
) => {
    const { children } = props;
    const { urlDirectory: urlDirectoryParam } = useParams();
    const user = useUserDataContext();

    // Clear the analytics view context when the prospecting app unmounts
    // (e.g. navigating to the in-queue app) so later events aren't tagged
    // with a stale region/fuel type. The clear is scoped to the value this
    // instance last set: React renders a replacement tree before running
    // this cleanup, so an unconditional clear could wipe the context a
    // newly-mounted provider instance just established.
    const lastSetTrackingContext = useRef<
        ProspectingViewTrackingContext | undefined
    >(undefined);
    useEffect(() => {
        return () => {
            if (lastSetTrackingContext.current !== undefined) {
                clearProspectingViewTrackingContext(
                    lastSetTrackingContext.current
                );
            }
        };
    }, []);

    if (urlDirectoryParam && isKnownUrlDirectoryType(urlDirectoryParam)) {
        const urlDirectory = convertToUrlDirectoryType(urlDirectoryParam);
        const region = getRegionFromUrlDirectory(urlDirectory);
        const generator = getGeneratorTypeFromUrlDirectory(urlDirectory);
        const scopeView = getCapacityCostsScopeViewFromRegionAndGenerator(
            region,
            generator
        );
        const acceptableScopes = getAcceptableScopesForRegionAndGenerator(
            user.scopes,
            region,
            generator
        );

        const contextValue: MapViewRoutingMetadataContext = {
            type: "mapView",
            urlDirectory,
            region,
            generator,
            componentConfig: getComponentConfig(region, generator),
            scopeView,
            acceptableScopes
        };

        // Set during render (not in an effect) so that events fired from
        // child components' mount effects are already tagged with the view.
        // This is safe to run repeatedly: it just overwrites a module-level
        // value with the same data derived from the URL.
        const trackingContext: ProspectingViewTrackingContext = {
            region,
            generatorType: generator,
            urlDirectory
        };
        setProspectingViewTrackingContext(trackingContext);
        lastSetTrackingContext.current = trackingContext;

        return (
            <RoutingMetadataContext.Provider value={contextValue}>
                {children}
            </RoutingMetadataContext.Provider>
        );
    }

    const contextValue: HomeViewRoutingMetadataContext = { type: "homeView" };
    // The home view has no region/fuel-type scope, so events fired there
    // should not carry view properties.
    setProspectingViewTrackingContext(undefined);
    lastSetTrackingContext.current = undefined;
    return (
        <RoutingMetadataContext.Provider value={contextValue}>
            {children}
        </RoutingMetadataContext.Provider>
    );
};

const useRoutingMetadata = (): RoutingMetadataContext => {
    const context = useContext(RoutingMetadataContext);
    if (context === undefined) {
        throw new Error(
            "useMapMetadata must be used within a RoutingMetadataProvider"
        );
    }

    return context;
};

const useMapViewRoutingMetadata = (): MapViewRoutingMetadataContext => {
    const context = useRoutingMetadata();

    if (context.type !== "mapView") {
        throw new Error(
            "useMapViewRoutingMetadata can only be used when RoutingMetadataContext.type === 'mapView'"
        );
    }

    return context as MapViewRoutingMetadataContext;
};

const usePricedConstraintConfig = (): PricedConstraintConfig => {
    const { componentConfig, region } = useMapViewRoutingMetadata();

    if (isPricedConstraintConfig(componentConfig)) {
        return componentConfig;
    }

    throw new Error(
        `Current region (${region}) does not have priced constraints`
    );
};

export {
    RoutingMetadataContext,
    RoutingMetadataProvider,
    useRoutingMetadata,
    useMapViewRoutingMetadata,
    usePricedConstraintConfig
};
